import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { fileUrl } from "@/lib/api";

export const COLOR_FIELDS = [
  ["bg", "Page background", "#FBF8F2"],
  ["alt", "Alternate section background", "#F3ECDD"],
  ["surface", "Cards", "#FFFFFF"],
  ["ink", "Headings & main text", "#14213D"],
  ["muted", "Secondary text", "#4A5568"],
  ["accent", "Gold accent text", "#8A6A12"],
  ["button", "Gold buttons", "#D4AF37"],
  ["title1", "Home title “Awa In”", "#0A0A0A"],
  ["title2", "Home title “Kelet.”", "#C8862F"],
];

export const DISPLAY_FONTS = ["Cormorant Garamond", "Playfair Display", "Lora", "Merriweather", "DM Serif Display", "Libre Baskerville", "EB Garamond"];
export const BODY_FONTS = ["Outfit", "Plus Jakarta Sans", "Manrope", "Nunito Sans", "Work Sans", "Source Sans 3", "Lato"];
export const DEFAULT_DISPLAY = DISPLAY_FONTS[0];
export const DEFAULT_BODY = BODY_FONTS[0];
const PRELOADED = new Set(["Cormorant Garamond", "Outfit", "Plus Jakarta Sans"]);

// Images the team can replace from the Visual Editor (key -> label, default file).
export const IMAGE_SLOTS = [
  ["logo", "Station logo", "/images/logo-round.png"],
  ["hero", "Home: big top photo", "/images/hero.jpg"],
  ["choice_world", "Home: “The World” photo", "/images/sunset.jpg"],
  ["choice_jesus", "Home: “Jesus” photo", "/images/horizon.jpg"],
  ["about_hero", "About: top photo", "/images/mic.jpg"],
  ["about_atoll", "About: atoll photo", "/images/atoll.jpg"],
  ["donate_hero", "Give: top photo", "/images/bible.jpg"],
  ["contact_hero", "Contact: top photo", "/images/palms.jpg"],
];
const IMG_DEFAULT = Object.fromEntries(IMAGE_SLOTS.map(([k, , d]) => [k, d]));

export const useImg = (key) => {
  const { settings } = useSite();
  if (!key) return "";
  return fileUrl(settings?.images?.[key] || IMG_DEFAULT[key]);
};

// Home page sections in their default order.
export const HOME_SECTIONS = [
  ["banner", "Banner slideshow"],
  ["hero", "Big title (Awa In Kelet)"],
  ["announcements", "Announcements"],
  ["marquee", "Scrolling words"],
  ["choice", "Jesus or the World"],
  ["featured", "Latest messages"],
  ["schedule", "Radio schedule"],
  ["give", "Give / donate call"],
];

// Puts sections that are new since the order was saved at their default spot.
export const withMissing = (list, keys, isKey = (x, k) => x === k, make = (k) => k) => {
  const out = [...list];
  keys.forEach((k, i) => {
    if (!out.some((x) => isKey(x, k))) out.splice(Math.min(i, out.length), 0, make(k));
  });
  return out;
};

export const homeOrder = (saved) => {
  const keys = HOME_SECTIONS.map(([k]) => k);
  const valid = (Array.isArray(saved) ? saved : []).filter((k) => keys.includes(k));
  return withMissing(valid, keys);
};

export const loadFont = (name) => {
  if (!name || PRELOADED.has(name)) return;
  const id = `gf-${name.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name)}:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&display=swap`;
  document.head.appendChild(link);
};

// Hides blocks, photos and text the team removed in the Visual Editor (public site only).
export const HiddenApplier = () => {
  const { settings } = useSite();
  const { pathname } = useLocation();
  const hidden = settings?.hidden || {};
  const css = pathname.startsWith("/admin") ? "" : Object.keys(hidden).filter((k) => hidden[k]).map((k) => {
    if (k.startsWith("b:")) return `[data-testid="${CSS.escape(k.slice(2))}"]`;
    if (k.startsWith("img:")) return `[data-ve-img="${CSS.escape(k.slice(4))}"]`;
    return "";
  }).filter(Boolean).join(",\n");
  return css ? <style data-testid="ve-hidden-style">{`${css} { display: none !important; }`}</style> : null;
};

export const humanize = (id = "") => id.replace(/[0-9a-f]{24}/g, "item").replace(/[-_]+/g, " ").trim();

// Applies the saved colors, fonts and text size to the public site (not to the portal).
export const ThemeApplier = () => {
  const { settings } = useSite();
  const { pathname } = useLocation();
  const theme = settings?.theme || {};
  const admin = pathname.startsWith("/admin");
  const key = JSON.stringify(theme);
  useEffect(() => {
    const root = document.documentElement.style;
    COLOR_FIELDS.forEach(([k]) => (admin || !theme[k] ? root.removeProperty(`--c-${k}`) : root.setProperty(`--c-${k}`, theme[k])));
    const fonts = [["--font-display", theme.font_display], ["--font-ui", theme.font_body], ["--font-body", theme.font_body]];
    fonts.forEach(([v, name]) => {
      if (admin || !name) root.removeProperty(v);
      else { loadFont(name); root.setProperty(v, `"${name}"`); }
    });
    const size = Number(theme.base_size);
    if (!admin && size >= 12 && size <= 22) root.setProperty("font-size", `${size}px`);
    else root.removeProperty("font-size");
  }, [key, admin]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};
